/**
 * URL utility functions.
 */

/**
 * Normalize a LinkedIn profile input to a full https:// URL.
 * Accepts bare usernames, linkedin.com/in/... paths, or full URLs.
 */
export function normalizeLinkedinInput(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^linkedin\.com\//i.test(trimmed) || /^www\.linkedin\.com\//i.test(trimmed)) {
    return `https://${trimmed}`;
  }
  return trimmed;
}

/**
 * Return true if the value is a valid http/https URL (or empty).
 */
export function isLikelyHttpUrl(value: string): boolean {
  if (!value.trim()) return true;
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Coerce a raw string to a full https:// URL, or return null if not URL-like.
 */
export function toExternalUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed || trimmed === 'No value yet') return null;

  let candidate = trimmed;
  if (!/^https?:\/\//i.test(candidate)) {
    if (/^www\./i.test(candidate) || /^[a-z0-9.-]+\.[a-z]{2,}(?:\/.*)?$/i.test(candidate)) {
      candidate = `https://${candidate}`;
    } else {
      return null;
    }
  }

  try {
    const parsed = new URL(candidate);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return parsed.toString();
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * DEV only: carry the current `?workspace=<slug>` param onto an in-app path, so
 * a full page load (auth links, email continue URLs) bootstraps the same
 * workspace's pre-auth config. In UAT/PROD the host picks the workspace, so the
 * path is returned unchanged.
 */
export function withDevWorkspaceParam(path: string): string {
  if (!import.meta.env.DEV) return path;
  const slug = new URLSearchParams(window.location.search).get('workspace');
  if (!slug) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}workspace=${encodeURIComponent(slug)}`;
}
